import { useQuery } from '@tanstack/react-query';
import { type AxiosError } from 'axios';
import ms from 'ms';
import { useEffect } from 'react';

import { toast } from '@hivebrite/design-system/Toast';

import { getMentoringProfileFromUser } from 'hb-react/frontoffice/apps/Mentoring/requests';
import {
  type MenteeProfile,
  type Program,
} from 'hb-react/frontoffice/apps/Mentoring/types';
import { getProgramTopicId } from 'hb-react/frontoffice/apps/Mentoring/utils';
import { QUERY_KEYS } from 'hb-react/shared/apps/Mentoring/constants';
import useFeatureFlag from 'hb-react/shared/hooks/useFeatureFlag';

interface GetMenteeProfileParams {
  program: Pick<Program, 'id' | 'mode' | 'parent_id' | 'parent_type'>;
  userId: number;
}

export const useGetMenteeProfileFromUser = ({
  program,
  userId,
}: GetMenteeProfileParams) => {
  const { flagEvMentoringAdminMatch } = useFeatureFlag();
  const {
    id: programId,
    parent_id: parentId,
    parent_type: parentType,
    mode,
  } = program;

  const getMenteeProfileFromUserQuery = useQuery<
    | MenteeProfile
    | {
        status: null;
      }
    | undefined,
    AxiosError
  >({
    queryKey: [QUERY_KEYS.menteeProfileFromUser, programId, userId],

    queryFn: ({ signal }) =>
      getMentoringProfileFromUser({
        routeApi: Routes.mentorings_program_user_mentee_profile_path(
          programId,
          userId,
        ),
        signal,
        topicId: getProgramTopicId({ parentId, parentType }),
      }),
    staleTime: ms('5m'),
    enabled: !!userId && flagEvMentoringAdminMatch && mode === 'admin_match',
  });

  const { error } = getMenteeProfileFromUserQuery;

  useEffect(() => {
    if (error?.response?.status === 500) {
      toast.error(__('Sorry, an error occurred'));
    }
  }, [error]);

  return getMenteeProfileFromUserQuery;
};
